import type { KeyboardEvent, ReactElement } from 'react';
import type { CandidateSkill, IntegrationMapEntry, SopStep } from '@opsrev/shared';
import { formatClock } from '../format';
import { EmptyState } from './Status';

export function IntegrationMapTable({
  entries,
}: {
  entries: IntegrationMapEntry[];
}): ReactElement {
  if (entries.length === 0) {
    return <EmptyState title="No systems detected" />;
  }
  return (
    <div className="table-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>System</th>
            <th>Surface</th>
            <th>Detected via</th>
            <th>Auth hints</th>
            <th>Actions observed</th>
            <th className="num">Confidence</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={`${e.system}-${i}`}>
              <td className="strong">{e.system}</td>
              <td>
                <span className={`tag surface-${e.surface}`}>{e.surface}</span>
              </td>
              <td>
                <div className="tag-row">
                  {e.detectedVia.map((d) => (
                    <span key={d} className="tag subtle">
                      {d}
                    </span>
                  ))}
                </div>
              </td>
              <td className="muted">{e.authHints || '—'}</td>
              <td>
                {e.actionsObserved.length === 0 ? (
                  <span className="muted">—</span>
                ) : (
                  <ul className="compact-list">
                    {e.actionsObserved.map((a, j) => (
                      <li key={j}>{a}</li>
                    ))}
                  </ul>
                )}
              </td>
              <td className="num">{Math.round(e.confidence * 100)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SopList({
  steps,
  onSelectScreenshot,
  seek,
}: {
  steps: SopStep[];
  onSelectScreenshot?: (ref: string) => void;
  /** Seek the session video to the given time (in seconds). */
  seek?: (sec: number) => void;
}): ReactElement {
  if (steps.length === 0) {
    return <EmptyState title="No SOP steps yet" />;
  }
  const ordered = [...steps].sort((a, b) => a.index - b.index);
  return (
    <ol className="sop-list">
      {ordered.map((s) => {
        const canJump = seek != null && typeof s.atTimeMs === 'number';
        const jump = canJump ? () => seek!((s.atTimeMs as number) / 1000) : undefined;
        return (
          <li key={s.index} className={`sop-step${canJump ? ' seekable' : ''}`}>
            <div
              className="sop-step-head"
              {...(jump
                ? {
                    role: 'button',
                    tabIndex: 0,
                    onClick: jump,
                    onKeyDown: (e: KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        jump();
                      }
                    },
                    title: 'Jump to this moment in the recording',
                  }
                : {})}
            >
              <span className="sop-index">{s.index + 1}</span>
              <h4>{s.title}</h4>
              {canJump ? (
                <span className="sop-jump">
                  <span className="clock" aria-hidden="true">
                    🕑
                  </span>
                  {formatClock((s.atTimeMs as number) / 1000)}
                </span>
              ) : null}
            </div>
            {s.intent ? (
              <p className="sop-why">
                <span className="sop-why-label">Why</span> {s.intent}
              </p>
            ) : null}
            {s.description ? <p className="sop-desc">{s.description}</p> : null}
            {s.narrationQuote ? (
              <blockquote className="sop-quote">
                {typeof s.atTimeMs === 'number' ? (
                  <span className="sop-quote-time">
                    <span className="clock" aria-hidden="true">
                      🕑
                    </span>
                    {formatClock(s.atTimeMs / 1000)}
                  </span>
                ) : null}
                <span className="sop-quote-text">“{s.narrationQuote}”</span>
              </blockquote>
            ) : null}
            <dl className="sop-meta">
              {s.userAction ? (
                <>
                  <dt>Action</dt>
                  <dd>{s.userAction}</dd>
                </>
              ) : null}
              {s.uiTarget ? (
                <>
                  <dt>UI target</dt>
                  <dd>{s.uiTarget}</dd>
                </>
              ) : null}
              {s.decisionBranches && s.decisionBranches.length > 0 ? (
                <>
                  <dt>Branches</dt>
                  <dd>
                    <ul className="compact-list">
                      {s.decisionBranches.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </dd>
                </>
              ) : null}
              {canJump ? (
                <>
                  <dt>Moment</dt>
                  <dd>
                    <button className="link-btn" onClick={jump}>
                      Jump to moment
                    </button>
                  </dd>
                </>
              ) : null}
              {s.screenshotRef ? (
                <>
                  <dt>Screenshot</dt>
                  <dd>
                    {onSelectScreenshot ? (
                      <button
                        className="link-btn"
                        onClick={() => onSelectScreenshot(s.screenshotRef as string)}
                      >
                        {s.screenshotRef}
                      </button>
                    ) : (
                      <code>{s.screenshotRef}</code>
                    )}
                  </dd>
                </>
              ) : null}
            </dl>
          </li>
        );
      })}
    </ol>
  );
}

/** Concise statement of what the operator is trying to accomplish + the problem solved. */
export function ObjectivePanel({ objective }: { objective: string }): ReactElement {
  return (
    <div className="objective-card">
      <h3>Objective</h3>
      <p>{objective}</p>
    </div>
  );
}

/** The discrete reusable capabilities the job decomposes into (step-2b inputs). */
export function SkillsList({ skills }: { skills: CandidateSkill[] }): ReactElement {
  if (skills.length === 0) {
    return <EmptyState title="No candidate skills identified" />;
  }
  return (
    <div className="skill-grid">
      {skills.map((skill, i) => (
        <SkillCard key={`${skill.name}-${i}`} skill={skill} />
      ))}
    </div>
  );
}

function SkillCard({ skill }: { skill: CandidateSkill }): ReactElement {
  return (
    <div className="skill-card">
      <div className="skill-card-head">
        <h4>{skill.name}</h4>
        <span className={`tag surface-${skill.automation}`}>{skill.automation}</span>
      </div>
      {skill.description ? <p className="skill-desc">{skill.description}</p> : null}
      {skill.systems.length > 0 ? (
        <div className="tag-row skill-systems">
          {skill.systems.map((sys, i) => (
            <span key={`${sys}-${i}`} className="tag subtle">
              {sys}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
